import { useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import toast from "react-hot-toast";
import { useAuthContext } from "../../context/auth";
import { useCurrency } from "../../utils";
import { calcularImpuesto } from "../../utils/calcularImpuesto";
import {
  calcularOperacionFactoring,
  describirConvencionFactoring,
  parsePct,
  tasaSeEligePorComprobante,
} from "../../utils/calculoFactoring";
import { resolverDocumentoFiscalId } from "../../utils/documentosFiscales";
import {
  crearOperacionFactoring,
  getOperacionFactoringPorFactura,
  getProveedoresFactoring,
} from "../../utils/factoring";
import { getLocale } from "../../utils/getLocale";
import { guardarFechaFactura } from "../../utils/guardarFechaFactura";
import { getCuentasBancarias } from "../../utils/movimientosBancarios";
import {
  getPlanCobros,
  crearPlanCobros,
  actualizarCuotaCobro,
  eliminarCuotaCobro,
  calcularOpsGuardadoSelectivo,
  getPlanPagos,
  getPlanPagosPorDocumento,
  crearPlanPagos,
  actualizarCuotaPago,
  eliminarCuotaPago,
} from "../../utils/planCobros";
import { MODOS_VALIDACION } from "../../utils/validarFechaDocumento";
import ComparadorTasaFactoring from "../ComparadorTasaFactoring";
import SelectorFechaDocumento from "../SelectorFechaDocumento";

// El label de "fecha" depende de cobro/pago (unificación de vocabulario, B.3 Commit 8, 2026-09-27:
// "Modificar fecha de cobro"/"Modificar fecha de pago", mismo texto que el resto de los modales de
// fecha migrados) — se calcula en el render, este placeholder nunca se usa tal cual.
const ACCIONES = [
  { key: "fecha", label: "" },
  { key: "cuotas", label: "En cuotas" },
  { key: "factoring", label: "Factoring" },
];

const formatMontoDisplay = (value, pais) => {
  if (!value && value !== 0) return "";
  if (typeof value === "string") return value;
  const locale = pais === "Chile" ? "es-CL" : "es-PE";
  return value.toLocaleString(locale, { maximumFractionDigits: 0 });
};

const formatPctDisplay = (value) => {
  if (!value && value !== 0) return "";
  return String(value).replace(".", ",");
};

// Porcentaje YA escalado a % (ej: 1.7700) con el separador decimal del país (Chile coma, Perú
// punto) vía el locale compartido getLocale (10.27); minFrac=maxFrac para no comerse ceros.
// Devuelve solo el número, sin "%". Espejo del helper homónimo en Factoring/index.js (mismo
// patrón que el formatMontoDisplay ya duplicado por-archivo).
const formatPctLocal = (pct, pais, decimals) =>
  new Intl.NumberFormat(getLocale(pais), {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(pct);

const parsePctInput = (displayValue) => {
  if (!displayValue) return "";
  const clean = displayValue.replace(/[^\d,.]/g, "");
  return clean.replace(",", ".");
};

const parseMontoInput = (displayValue, moneda) => {
  const clean = displayValue.replace(/[^\d,.]/g, "");
  if (!clean) return "";
  const lastDot = clean.lastIndexOf(".");
  const lastComma = clean.lastIndexOf(",");
  let numStr;
  if (lastDot >= 0 && lastComma >= 0) {
    numStr = lastDot > lastComma ? clean.replace(/,/g, "") : clean.replace(/\./g, "").replace(",", ".");
  } else if (lastComma >= 0) {
    numStr = clean.replace(",", ".");
  } else if (lastDot >= 0) {
    numStr = moneda === "CLP" || moneda === "PEN" ? clean.replace(/\./g, "") : clean;
  } else {
    numStr = clean;
  }
  if (numStr.endsWith(".")) return numStr;
  const num = parseFloat(numStr);
  return isNaN(num) ? "" : num;
};

// Una cuota pagada/cobrada está congelada: tiene un movimiento bancario real vinculado (FK
// movimientos_bancarios.plan_pago_cuota_id). No se edita, ni se borra, ni se "reinicia" — borrarla
// dejaría el pago huérfano. Fuente única del concepto (10.1).
const esCuotaPagada = (c) => c?.estado === "pagado" || c?.estado === "cobrado";

const PlanCuotasModal = ({ isOpen, onClose, tipo, factura, onSuccess, accionInicial = "fecha" }) => {
  const { id: userId, pais } = useAuthContext();
  const { formatAmount } = useCurrency();

  const esCobro = tipo === "cobro";
  // Factura externa (proveedor sin DTE SII, ej. OnSign): id numérico de facturas_externas,
  // sin folio ni razon_social_emisor. Solo puede planificar fecha de pago (plazopago).
  const esExterna = !!factura?.es_externa;
  // Boleta de honorarios (Fase 5b): cuelga de documento_id (tipo='boleta_honorario'). Su id numérico
  // viene en factura.boleta_id (el id del entry es string "boleta-<id>"). Monto en monto_bruto, CLP.
  // Desde Fase 4 de "Futuros: reagendar" (2026-09-25, migración 20260925c) tiene `plazopago` en el
  // esquema. P2.105 (B.3 Commit 7, 2026-09-27): CERRADO — este modal ahora sí ofrece "Ajustar fecha"
  // para una boleta SIN plan, paridad con la externa (ver `accionesVisibles` más abajo).
  const esBoleta = !!factura?.es_boleta;
  // Externa y boleta cuelgan de documento_id (no de factura_sii_recibida_id).
  const esDocId = esExterna || esBoleta;
  // id numérico del documento base: boleta = factura.boleta_id ; externa/SII = factura.id.
  const origenId = esBoleta ? factura?.boleta_id : factura?.id;
  // tipo en documentos_fiscales.
  const docTipo = esBoleta
    ? "boleta_honorario"
    : esExterna
      ? "factura_externa"
      : esCobro
        ? "sii_emitida"
        : "sii_recibida";
  const monto = Number((esBoleta ? factura?.monto_bruto : factura?.monto_total) || 0);
  const moneda = esBoleta ? "CLP" : factura?.moneda || "CLP";
  const fmt = (v) => formatAmount(v, moneda);
  const razonSocial = esCobro ? factura?.razon_social_receptor : factura?.razon_social_emisor;
  // Externa: proveedor_nombre + numero_documento. Boleta: emisor + "Boleta N°". SII: razón + folio.
  const titulo = esBoleta
    ? `${factura?.razon_social_receptor || "Boleta"} | ${factura?.folio || ""}`
    : esExterna
      ? factura?.numero_documento
        ? `${factura.proveedor_nombre} | ${factura.numero_documento}`
        : factura?.proveedor_nombre || "Factura externa"
      : razonSocial
        ? `${razonSocial} | Folio ${factura?.folio}`
        : `Folio ${factura?.folio}`;
  const fechaEmisionFmt = factura?.fecha_emision
    ? (() => {
        const [y, m, d] = factura.fecha_emision.substring(0, 10).split("-");
        return `${d}/${m}/${y}`;
      })()
    : null;

  // Acción activa
  const [accion, setAccion] = useState(accionInicial);

  // Estado compartido
  const [loading, setLoading] = useState(false);

  // Acción 1 — Ajustar fecha
  const [fechaAjuste, setFechaAjuste] = useState("");

  // Acción 2 — Cuotas
  const [cuotas, setCuotas] = useState([]);
  const [numCuotas, setNumCuotas] = useState(2);
  // Snapshot del plan cargado de DB (con id + estado) para el diff del guardado selectivo.
  const [cuotasOriginales, setCuotasOriginales] = useState([]);

  // Acción 3 — Factoring
  const [proveedores, setProveedores] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [operacionExistente, setOperacionExistente] = useState(null);
  // Tipo de tasa elegido contra el comprobante, solo para proveedores con dos fórmulas (Fingo /
  // Santander, P1.17). "" = sin elección → no se puede registrar.
  const [tipoTasaElegido, setTipoTasaElegido] = useState("");
  const [formFactoring, setFormFactoring] = useState({
    proveedor_factoring_id: "",
    pct_anticipo: "",
    tasa: "",
    tasa_mora: "",
    comision_proveedor: "",
    fecha_operacion: new Date().toISOString().split("T")[0],
    fecha_cobro_final: "",
    cuenta_bancaria_id: "",
  });

  // Cargar datos al abrir
  useEffect(() => {
    if (!isOpen || !factura) return;
    setAccion(factura?.es_externa ? "fecha" : factura?.es_factoring ? "factoring" : accionInicial);
    setOperacionExistente(null);

    const fechaInicial = esCobro ? factura.plazocobro || "" : factura.plazopago || factura.fecha_display || "";
    setFechaAjuste(fechaInicial);

    setFormFactoring((prev) => ({
      ...prev,
      fecha_cobro_final: factura.plazocobro || "",
      proveedor_factoring_id: "",
      pct_anticipo: "",
      tasa: "",
      tasa_mora: "",
      comision_proveedor: "",
    }));
    setTipoTasaElegido("");

    if (!esCobro && !esExterna) {
      // Pago SII o boleta (P2.105): si ya tiene plan de cuotas, abre directo en ese tab
      // (abrirEnCuotasSiHayPlan llama a cargarCuotas); sin plan, se queda en "fecha" (accionInicial),
      // nunca se fuerza "cuotas" a ciegas.
      abrirEnCuotasSiHayPlan();
    } else {
      cargarCuotas();
      if (esCobro) {
        cargarFactoring();
        cargarOperacionExistente();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, factura]);

  useEffect(() => {
    if (cuentas.length === 0 || !factura?.moneda) return;
    const monedaFactura = factura.moneda;
    const cuentasMoneda = cuentas.filter((c) => c.moneda === monedaFactura);
    if (cuentasMoneda.length === 1) {
      setFormFactoring((p) => ({
        ...p,
        cuenta_bancaria_id: String(cuentasMoneda[0].id),
      }));
    }
  }, [cuentas, factura?.moneda]);

  // Fase 5a/5b — identidad de documento de una EXTERNA (tipo='factura_externa', origen=factura.id) o
  // BOLETA (tipo='boleta_honorario', origen=factura.boleta_id) en el supertipo documentos_fiscales.
  // Su cronograma cuelga de documento_id, no de factura_sii_recibida_id (NULL). No-silencioso si falla.
  const resolverDocId = async () => {
    if (!esDocId) return null;
    return resolverDocumentoFiscalId(docTipo, origenId);
  };

  const cargarCuotas = async () => {
    if (factura?.es_factoring) return [];
    try {
      let data;
      if (esCobro) {
        data = await getPlanCobros(factura.id);
      } else if (esDocId) {
        // Externa/boleta: las cuotas se leen por documento_id (su factura_sii_recibida_id es NULL).
        const docId = await resolverDocId();
        data = docId != null ? await getPlanPagosPorDocumento(docId) : [];
      } else {
        data = await getPlanPagos(factura.id);
      }
      setCuotas(data);
      setCuotasOriginales(data);
      return data;
    } catch {
      toast.error("Error cargando cuotas");
      return [];
    }
  };

  // Pago SII con plan de cuotas ya armado → abrir en el tab "En cuotas". "Ajustar fecha" no compromete
  // nada, por eso no se detecta. (Hasta 2026-09-25 esto también detectaba caja_chica_gastos para lockear
  // el tab "Caja Chica" contra "En cuotas"; el tab se retiró — caja chica se asigna desde el kebab de
  // "Por pagar" con AsignarCajaChicaModal — y una factura en caja chica sale de "Por pagar", así que no
  // llega a este modal en tipo pago: el guard de exclusión cuotas↔caja chica vive en
  // asignarFacturaCajaChica.)
  const abrirEnCuotasSiHayPlan = async () => {
    const cuotasPlan = await cargarCuotas();
    if (cuotasPlan.length > 0) setAccion("cuotas");
  };

  const cargarFactoring = async () => {
    try {
      const [provs, ctas] = await Promise.all([getProveedoresFactoring(pais), getCuentasBancarias(pais)]);
      setProveedores(provs);
      setCuentas(ctas);
    } catch {
      toast.error("Error cargando datos de factoring");
    }
  };

  const cargarOperacionExistente = async () => {
    if (!esCobro || !factura?.id) return;
    try {
      const facturaRealId = factura.factura_sii_emitida_id || factura.id;
      const op = await getOperacionFactoringPorFactura(facturaRealId);
      setOperacionExistente(op);
    } catch {
      setOperacionExistente(null);
    }
  };

  // Guard duro contra ids que no resuelven a una fila real. Las entries sintéticas del panel
  // (recurrente-..., residuo-..., edificio-..., capex-..., etc.) tienen id string: escribir con
  // ese id provoca error 22P02 de Postgres (casteo a integer) o un match cruzado accidental.
  // Se valida el id NUMÉRICO real del documento: boleta = origenId (factura.boleta_id, porque su
  // factura.id es el string "boleta-<id>"); externa/SII = factura.id.
  const idEsEditable = () => {
    const idReal = esBoleta ? origenId : factura?.id;
    if (typeof idReal !== "number") {
      toast.error("Esta entry no es editable: no corresponde a una factura real.");
      return false;
    }
    return true;
  };

  // ── Acción 1: Ajustar fecha ──────────────────────────────────────────────
  // Lógica real extraída a src/utils/guardarFechaFactura.js (Fase 1 de "Futuros: reagendar",
  // 2026-09-23) — reusada tal cual por el despachador `reagendarEntryFuturo` del panel de Futuros
  // (10.1). Este handler queda como wrapper: UI (toast, loading, onSuccess/onClose) sin lógica propia.
  const handleGuardarFecha = async () => {
    if (!fechaAjuste) return toast.error("Ingresá una fecha");
    if (!idEsEditable()) return;
    setLoading(true);
    try {
      // B.3 Commit 7 (2026-09-27, camino #8): MIGRADO a modo ESTRICTO — el calendario compartido
      // (SelectorFechaDocumento) ya deshabilita visualmente lo mismo que el writer rechaza (10.1).
      const resultado = await guardarFechaFactura({
        factura,
        tipo,
        nuevaFecha: fechaAjuste,
        modo: MODOS_VALIDACION.ESTRICTO,
        pais,
      });
      if (resultado.error) {
        toast.error(resultado.error);
        return;
      }
      toast.success("Fecha actualizada");
      // Patch puntual (Fase 2A): el padre mueve la fila por id de factura (plazocobro/plazopago) sin
      // refetch. `nuevaFecha` es la fecha efectiva recién persistida.
      onSuccess?.({ accion: "fecha", nuevaFecha: fechaAjuste });
      onClose();
    } catch (err) {
      toast.error(`Error al guardar: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // ── Acción 2: Cuotas ─────────────────────────────────────────────────────
  const generarCuotas = () => {
    if (numCuotas < 2 || numCuotas > 12) return toast.error("Entre 2 y 12 cuotas");
    // Se reparte SOLO el saldo pendiente (total de la factura − Σ cuotas ya pagadas), nunca el total:
    // repartir el total re-contaría lo ya pagado e infla el plan (causa raíz de las 6 cuotas de la
    // 1143). Las pagadas se preservan tal cual (congeladas), no se re-generan (10.103).
    const pagadas = cuotas.filter(esCuotaPagada);
    const montoPagado = pagadas.reduce((s, c) => s + Number(c.monto_cuota || 0), 0);
    const montoARepartir = monto - montoPagado;
    if (montoARepartir <= 0) {
      return toast.error("Las cuotas pagadas ya cubren el total de la factura; no hay saldo pendiente para repartir.");
    }
    const montoPorCuota = Math.floor(montoARepartir / numCuotas);
    const diferencia = montoARepartir - montoPorCuota * numCuotas;
    const hoy = new Date();
    const nuevas = Array.from({ length: numCuotas }, (_, i) => {
      const fecha = new Date(hoy);
      fecha.setMonth(fecha.getMonth() + i + 1);
      return {
        // numero_cuota provisional (display); el guardado asigna el definitivo (max existente + N)
        // a las nuevas y respeta el de las pagadas — nunca renumera por fecha (10.190 / índice único).
        numero_cuota: i + 1,
        monto_cuota: i === numCuotas - 1 ? montoPorCuota + diferencia : montoPorCuota,
        fecha_vencimiento: fecha.toISOString().split("T")[0],
        estado: "pendiente",
      };
    });
    // El estado refleja EXACTAMENTE lo que se persiste: pagadas (intactas) + nuevas pendientes. Así la
    // validación de cuadre suma lo mismo que se guarda, sin duplicar los vencimientos de las pagadas.
    setCuotas([...pagadas, ...nuevas]);
  };

  const handleCambiarCuota = (idx, field, value) => {
    setCuotas((prev) => prev.map((c, i) => (i === idx ? { ...c, [field]: value } : c)));
  };

  // Guardado SELECTIVO de cuotas de pago SII (espejo de PlanPagosActivoModal.handleGuardar).
  // `numero_cuota` es la IDENTIDAD de la cuota, NO un índice de orden: editar fecha o monto NUNCA
  // lo renumera. Renumerar por posición (fecha) transponía ordinales entre filas y, como cada
  // actualizarCuotaPago es un request PostgREST independiente/no atómico contra un índice único
  // (factura, numero_cuota) NO diferible, dejaba un estado intermedio con dos filas en el mismo
  // ordinal → 23505 (postergar la cuota 1 más allá de la pagada). De las pagadas no se toca nada
  // en absoluto (estado, monto, fecha, numero_cuota ni el vínculo FK
  // movimientos_bancarios.plan_pago_cuota_id). El orden cronológico es puro display; ningún
  // consumidor (FlujoCajaV2 ordena por fecha_vencimiento, el label "N/M" muestra el numero tal cual)
  // depende de que numero_cuota sea contiguo ni ascendente por fecha.
  const guardarCuotasPagoSelectivo = async () => {
    // Fase 5a/5b — identidad del documento. Externa/boleta: cuelgan de documento_id
    // (factura_sii_recibida_id NULL). SII recibida: sigue por factura_sii_recibida_id (crearPlanPagos
    // le agrega documento_id).
    const docIdDoc = esDocId ? await resolverDocId() : null;
    if (esDocId && docIdDoc == null) {
      // No-silencioso (10.177): sin documento no se puede anclar el plan. No inserta filas huérfanas.
      toast.error("No se pudo resolver el documento (externa/boleta). No se guardó el plan.");
      return;
    }
    const esPagada = esCuotaPagada;
    const pendientesState = cuotas.filter((c) => !esPagada(c));
    const originalesPendientes = cuotasOriginales.filter((c) => !esPagada(c));
    // numero_cuota de una pendiente nueva = max de TODO el snapshot (pagadas + pendientes) +
    // secuencial → siempre por encima de cualquier ordinal existente, sin colisionar con UPDATEs
    // ni con DELETEs concurrentes del mismo Promise.all.
    const maxNumero = cuotasOriginales.reduce((m, c) => Math.max(m, Number(c.numero_cuota) || 0), 0);
    const ops = [];

    // UPDATE: pendientes con id, SOLO los campos que cambiaron (fecha/monto). numero_cuota queda fijo.
    for (const c of pendientesState) {
      if (!c.id) continue;
      const orig = cuotasOriginales.find((o) => o.id === c.id);
      const cambios = {};
      if (orig && String(orig.fecha_vencimiento) !== String(c.fecha_vencimiento)) {
        cambios.fecha_vencimiento = c.fecha_vencimiento;
      }
      if (orig && Number(orig.monto_cuota) !== Number(c.monto_cuota)) {
        cambios.monto_cuota = Number(c.monto_cuota);
      }
      if (Object.keys(cambios).length > 0)
        ops.push(actualizarCuotaPago(c.id, cambios, { modo: MODOS_VALIDACION.DATO_DOCUMENTO }));
    }

    // INSERT: pendientes nuevas (sin id). numero_cuota estable = max existente + secuencial.
    const nuevas = pendientesState.filter((c) => !c.id);
    if (nuevas.length > 0) {
      ops.push(
        crearPlanPagos(
          nuevas.map((c, idx) => ({
            pais,
            // Externa/boleta: factura_sii_recibida_id NULL + documento_id explícito. SII: la FK typed
            // (crearPlanPagos le resuelve documento_id por 'sii_recibida' vía conDocumentoId).
            factura_sii_recibida_id: esDocId ? null : factura.id,
            ...(esDocId ? { documento_id: docIdDoc } : {}),
            numero_cuota: maxNumero + idx + 1,
            monto_cuota: Number(c.monto_cuota),
            fecha_vencimiento: c.fecha_vencimiento,
            estado: "pendiente",
            created_by: userId,
          })),
          { modo: MODOS_VALIDACION.DATO_DOCUMENTO }
        )
      );
    }

    // DELETE: pendientes que estaban en DB y el usuario quitó del state (guard: solo pendientes).
    const idsEnState = new Set(pendientesState.filter((c) => c.id).map((c) => c.id));
    for (const o of originalesPendientes) {
      if (o.id && !idsEnState.has(o.id)) ops.push(eliminarCuotaPago(o.id));
    }

    await Promise.all(ops);
  };

  // Guardado SELECTIVO de cuotas de cobro (espejo de guardarCuotasPagoSelectivo). Auditoría 3b/3c/4,
  // Commit 2 (2026-09-25): reemplaza el borra-todo-y-reinserta que tenía "Por cobrar" — ese patrón
  // borraba TODAS las cuotas y las volvía a insertar, así que la cuota NO tocada también se
  // re-insertaba con id nuevo → el trigger capturar_fecha_vencimiento_original la trataba como fila
  // recién nacida y NUNCA capturaba un original real (la pill azul de 10.278 jamás se encendía para
  // cobro, aunque el usuario reagendara una cuota y volviera a guardar el plan). El guardado selectivo
  // deja la fila (y su id, y su fecha_vencimiento_original ya capturada) intacta si no cambió.
  //
  // Consumidor único (grep 2026-09-25, 10.243): handleGuardarCuotas, rama esCobro — sin otro caller que
  // dependa de la atomicidad del delete+reinsert viejo.
  //
  // Non-atomicidad conocida — mismo hallazgo que guardarCuotasPagoSelectivo (que YA corre así en
  // producción para pago, no es un riesgo nuevo que este commit introduzca): cada op de `ops` es un
  // request PostgREST independiente, Promise.all NO las corre en una sola transacción. El candado
  // DEFERRABLE trg_plan_cobros_facturas_suma solo difiere DENTRO de la transacción de cada request —
  // si el usuario edita monto_cuota de 2+ cuotas EXISTENTES en la misma pasada de "Guardar" (mover
  // plata entre cuotas), el orden de commit de esos UPDATEs independientes podría, en teoría, rechazar
  // uno contra un estado intermedio que no cuadra, aunque el lote completo sí cuadre al final. El viejo
  // delete-all+reinsert de cobro era, de hecho, más seguro para ESTA cuenta específica (el DELETE deja
  // v_n=0 → el trigger no valida nada; el INSERT único mete TODAS las filas nuevas en una sola
  // transacción → el trigger las valida juntas, atómico) — pero no conservaba el original, que es
  // exactamente el bug que este commit cierra. Se prioriza conservar el original sobre esa atomicidad
  // incidental del patrón viejo; la propuesta real (aplica a pago Y a cobro) es una RPC que agrupe
  // UPDATE+INSERT+DELETE en una sola transacción server-side — documentada en el roadmap, no
  // implementada acá (fuera de alcance de este commit).
  const guardarCuotasCobroSelectivo = async () => {
    const pendientesState = cuotas.filter((c) => !esCuotaPagada(c));
    const originalesPendientes = cuotasOriginales.filter((c) => !esCuotaPagada(c));
    // Cómputo puro (qué UPDATE/INSERT/DELETE hace falta) extraído a planCobros.js — testeado ahí sin
    // mockear Supabase (calcularOpsGuardadoSelectivo). Acá solo se traducen a los calls reales.
    const { actualizaciones, nuevas, eliminarIds } = calcularOpsGuardadoSelectivo(
      pendientesState,
      originalesPendientes,
      cuotasOriginales
    );

    const ops = [
      ...actualizaciones.map((u) => actualizarCuotaCobro(u.id, u.cambios, { modo: MODOS_VALIDACION.DATO_DOCUMENTO })),
      ...eliminarIds.map((id) => eliminarCuotaCobro(id)),
    ];
    if (nuevas.length > 0) {
      ops.push(
        crearPlanCobros(
          nuevas.map((n) => ({
            pais,
            factura_sii_emitida_id: factura.id,
            ...n,
            estado: "pendiente",
            created_by: userId,
          })),
          { modo: MODOS_VALIDACION.DATO_DOCUMENTO }
        )
      );
    }

    await Promise.all(ops);
  };

  const handleGuardarCuotas = async () => {
    if (cuotas.length === 0) return toast.error("Generá al menos una cuota");
    // Validar EXACTAMENTE el conjunto que se va a PERSISTIR, no el state de la UI. Ese era el agujero:
    // tras "Reiniciar" el state no incluía las pagadas, pero el guardado selectivo las re-inyecta
    // del snapshot → la validación pasaba con el total, y se guardaba el total + las pagadas (inflado).
    // Pago Y cobro (Commit 2 de la auditoría 3b/3c/4, ambos guardados selectivos): persiste [pagadas/
    // cobradas del snapshot (intactas) + pendientes del state].
    const setPersistido = [...cuotasOriginales.filter(esCuotaPagada), ...cuotas.filter((c) => !esCuotaPagada(c))];
    const totalPersistido = setPersistido.reduce((s, c) => s + Number(c.monto_cuota || 0), 0);
    // Sin fallo silencioso (10.177): si no cuadra, no se guarda y la diferencia queda visible.
    if (Math.abs(totalPersistido - monto) > 1) {
      const diff = totalPersistido - monto;
      return toast.error(
        `El plan (${fmt(totalPersistido)}) no cuadra con la factura (${fmt(monto)}). Diferencia: ${fmt(diff)}.`
      );
    }
    if (!idEsEditable()) return;
    setLoading(true);
    try {
      await (esCobro ? guardarCuotasCobroSelectivo() : guardarCuotasPagoSelectivo());
      toast.success("Plan guardado");
      // "Cuotas" queda fuera de Fase 2A (inserta/borra filas con ids nuevos): el padre hace refetch.
      onSuccess?.({ accion: "cuotas" });
      onClose();
    } catch (err) {
      toast.error(`Error al guardar: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // ── Acción 3: Factoring ─────────────────────────────────────────────────
  // Cálculo por la FUENTE ÚNICA src/utils/calculoFactoring.js (10.1 / 10.19 / 10.243), con la
  // convención del proveedor seleccionado (tipo de tasa, conteo de días, redondeo por documento).
  // Antes esta fórmula vivía duplicada acá y en Factoring/index.js.
  const proveedorFactoringSel = proveedores.find((p) => p.id === parseInt(formFactoring.proveedor_factoring_id, 10));
  const pctAnticipo = parsePct(formFactoring.pct_anticipo);
  const tasaF = parsePct(formFactoring.tasa);
  const tasaMoraF = parsePct(formFactoring.tasa_mora);
  const comisionProveedor =
    typeof formFactoring.comision_proveedor === "string" ? 0 : Number(formFactoring.comision_proveedor || 0);

  // Fingo / Santander (P1.17): el tipo de tasa se elige por operación contra el comprobante → se
  // calcula con las dos (misma fuente única, dos llamadas) y se guarda la elegida. Mismo criterio
  // que NuevaOperacionModal (Factoring/index.js).
  const eligeTasaPorComprobante = tasaSeEligePorComprobante(proveedorFactoringSel?.id);
  const tipoTasa = eligeTasaPorComprobante ? tipoTasaElegido : proveedorFactoringSel?.tipo_tasa || "vencida";
  const calcularConTasa = (tipo) =>
    calcularOperacionFactoring({
      monto,
      pctAnticipo,
      tasaMensual: tasaF,
      comisionProveedor,
      fechaOperacion: formFactoring.fecha_operacion,
      fechaVencimiento: formFactoring.fecha_cobro_final,
      tipoTasa: tipo,
      incluyeDiaOperacion: proveedorFactoringSel?.plazo_incluye_dia_operacion ?? true,
      redondeoPorDocumento: proveedorFactoringSel?.redondeo_por_documento ?? false,
      // Se cede una sola factura desde este modal → un documento (habilita el redondeo por
      // documento del proveedor si corresponde; con un doc equivale al agregado).
      documentos: [monto],
    });
  const resultadosPorTasa = eligeTasaPorComprobante
    ? { anticipada: calcularConTasa("anticipada"), vencida: calcularConTasa("vencida") }
    : null;
  // Sin elección, capital/plazo/excedentes no dependen del tipo de tasa; diferencia, giro e IVA
  // solo se guardan con una elección hecha (guard en handleGuardarFactoring).
  const { plazoDias, capitalAdelantado, diferenciaPrecio, totalCobros, giroARecibir, excedentes } = calcularConTasa(
    tipoTasa || "vencida"
  );
  // ivaFinanciamiento se calcula y persiste en la operación, pero no se muestra en el modal.
  // Se usará después cuando llegue la factura SII de Chita para reconciliar.
  const { impuesto: ivaFinanciamiento } = calcularImpuesto(diferenciaPrecio + comisionProveedor, pais);

  const handleProveedorChange = (id) => {
    const prov = proveedores.find((p) => p.id === parseInt(id, 10));
    setTipoTasaElegido("");
    setFormFactoring((prev) => ({
      ...prev,
      proveedor_factoring_id: id,
      tasa_mora: prov && prov.tasa_mora ? formatPctLocal(prov.tasa_mora * 100, pais, 2) : "",
      pct_anticipo: prov
        ? prov.pct_anticipo_habitual
          ? formatPctLocal(prov.pct_anticipo_habitual * 100, pais, 1)
          : "95"
        : "",
    }));
  };

  const handleGuardarFactoring = async () => {
    if (!formFactoring.proveedor_factoring_id) return toast.error("Seleccioná un proveedor");
    if (!formFactoring.cuenta_bancaria_id) return toast.error("Seleccioná una cuenta bancaria");
    if (!pctAnticipo) return toast.error("Ingresá el % de anticipo");
    if (!plazoDias) return toast.error("Ingresá la fecha de vencimiento");
    if (!tipoTasa) return toast.error("Elegí el cálculo que coincide con el Monto a Recibir de tu comprobante");
    if (!idEsEditable()) return;
    setLoading(true);
    try {
      await crearOperacionFactoring({
        pais,
        factura_sii_emitida_id: factura.id,
        proveedor_factoring_id: parseInt(formFactoring.proveedor_factoring_id, 10),
        monto_factura: monto,
        pct_anticipo: pctAnticipo,
        capital_adelantado: capitalAdelantado,
        tasa: tasaF,
        tasa_mora: tasaMoraF,
        plazo_dias: plazoDias,
        tipo_tasa: tipoTasa,
        costo_financiero: diferenciaPrecio,
        comision_proveedor: comisionProveedor,
        iva_financiamiento: ivaFinanciamiento,
        giro_a_recibir: giroARecibir,
        fecha_operacion: formFactoring.fecha_operacion,
        fecha_cobro_final: formFactoring.fecha_cobro_final || null,
        cuenta_bancaria_id: parseInt(formFactoring.cuenta_bancaria_id, 10),
        created_by: userId,
        estado: "activa",
      });
      toast.success("Operación de factoring registrada");
      // "Factoring" queda fuera de Fase 2A (op con id/derivados nuevos): el padre hace refetch.
      onSuccess?.({ accion: "factoring" });
      onClose();
    } catch (err) {
      toast.error("Error al registrar factoring: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  if (!factura) return null;

  const totalCuotas = cuotas.reduce((s, c) => s + Number(c.monto_cuota || 0), 0);
  const diferenciaCuotas = monto - totalCuotas;
  // Estado de las cuotas para la regeneración coherente: hay cuotas pendientes en el state?, cuánto
  // cubren las pagadas, y cuánto es el saldo pendiente que "Generar" debe repartir. "Generar" se
  // ofrece cuando NO hay pendientes (con o sin pagadas); las pagadas siempre se muestran, congeladas.
  const hayPendientes = cuotas.some((c) => !esCuotaPagada(c));
  const montoPagado = cuotas.filter(esCuotaPagada).reduce((s, c) => s + Number(c.monto_cuota || 0), 0);
  const montoPendiente = monto - montoPagado;

  // Tabs visibles según tipo
  // Boleta (Fase 5b) y externa (Fase 5a): "Ajustar fecha" + "En cuotas" (sin factoring; cronograma
  // por documento_id). P2.105 (B.3 Commit 7, 2026-09-27): CERRADO — la boleta gana "Ajustar fecha",
  // paridad con la externa (antes SOLO "En cuotas"). SII pago: sin factoring. SII cobro: todo.
  const accionesVisibles =
    esBoleta || esExterna
      ? ACCIONES.filter((a) => a.key === "fecha" || a.key === "cuotas")
      : esCobro
        ? ACCIONES
        : ACCIONES.filter((a) => a.key !== "factoring");

  return (
    <Modal show={isOpen} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "0.95rem" }}>
          {titulo}
          {fechaEmisionFmt && (
            <span style={{ fontSize: "0.75rem", fontWeight: 400, color: "#888", marginLeft: 8 }}>
              · Fac. {fechaEmisionFmt}
            </span>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Tabs de acción */}
        <div style={{ display: "flex", width: "100%", marginBottom: 20, gap: 8 }}>
          {accionesVisibles.map((a) => {
            const tabDeshabilitada = factura?.es_factoring && a.key !== "factoring";
            return (
              <button
                key={a.key}
                type="button"
                onClick={() => setAccion(a.key)}
                disabled={tabDeshabilitada}
                style={{
                  flex: 1,
                  padding: "7px 0",
                  fontSize: "0.82rem",
                  fontWeight: 600,
                  border: "1px solid #dee2e6",
                  borderRadius: 6,
                  cursor: tabDeshabilitada ? "default" : "pointer",
                  background: accion === a.key ? "#2c3e50" : "transparent",
                  color: accion === a.key ? "#fff" : "#666",
                  textAlign: "center",
                  opacity: tabDeshabilitada ? 0.4 : 1,
                  pointerEvents: tabDeshabilitada ? "none" : "auto",
                }}
              >
                {a.key === "fecha" ? (esCobro ? "Modificar fecha de cobro" : "Modificar fecha de pago") : a.label}
              </button>
            );
          })}
        </div>

        {/* ── Acción 1: Ajustar fecha ── */}
        {accion === "fecha" && (
          <div>
            <label style={{ fontSize: "0.82rem", fontWeight: 600, display: "block", marginBottom: 6 }}>
              {esCobro ? "Nueva fecha de cobro" : "Nueva fecha de pago"}
            </label>
            <SelectorFechaDocumento
              value={fechaAjuste}
              onChange={(nuevaFecha) => setFechaAjuste(nuevaFecha || "")}
              pais={pais}
              modo={MODOS_VALIDACION.ESTRICTO}
              className="form-control form-control-sm"
              style={{ maxWidth: 200 }}
            />
          </div>
        )}

        {/* ── Acción 2: Cuotas ── */}
        {accion === "cuotas" && (
          <>
            {!hayPendientes && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <span style={{ fontSize: "0.85rem" }}>Generar</span>
                  <input
                    type="number"
                    min={2}
                    max={12}
                    value={numCuotas}
                    onChange={(e) => setNumCuotas(parseInt(e.target.value))}
                    className="form-control form-control-sm"
                    style={{ width: 60 }}
                  />
                  <span style={{ fontSize: "0.85rem" }}>cuotas iguales</span>
                  <button className="btn btn-sm btn-primary" onClick={generarCuotas}>
                    Generar
                  </button>
                </div>
                {/* Con cuotas pagadas presentes, "Generar" reparte SOLO el saldo pendiente. */}
                {montoPagado > 0 && (
                  <div style={{ fontSize: "0.78rem", color: "#666", marginTop: 6 }}>
                    Ya pagado: <strong>{fmt(montoPagado)}</strong> · saldo pendiente a repartir:{" "}
                    <strong>{fmt(montoPendiente)}</strong>
                  </div>
                )}
              </div>
            )}
            {cuotas.length > 0 && (
              <>
                <table className="table table-sm table-bordered" style={{ fontSize: "0.83rem" }}>
                  <thead className="table-dark">
                    <tr>
                      <th>N°</th>
                      <th>Fecha vencimiento</th>
                      <th style={{ textAlign: "right" }}>Monto</th>
                      <th style={{ textAlign: "center" }}>Estado</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {/* Solo PRESENTACIÓN: se ordena una copia por fecha_vencimiento asc (las filas se ven
                        siempre cronológicas, aunque una cuota se haya postergado) y la columna N° muestra el
                        numero_cuota REAL (identidad fija), no la posición. Puede verse no-secuencial si una
                        cuota se movió — eso es correcto. Los handlers mutan el state por su índice ORIGINAL
                        (origIdx), no por el de display; el key es estable (id o "nueva-N") para no perder
                        foco al reordenar. El state (`cuotas`) y el guardado no se tocan. */}
                    {cuotas
                      .map((c, origIdx) => ({ c, origIdx }))
                      .sort((a, b) => {
                        const fa = String(a.c.fecha_vencimiento || "");
                        const fb = String(b.c.fecha_vencimiento || "");
                        if (fa !== fb) return fa < fb ? -1 : 1;
                        return a.origIdx - b.origIdx;
                      })
                      .map(({ c, origIdx }) => {
                        // Cuota pagada/cobrada: congelada — vinculada a un movimiento histórico
                        // (FK movimientos_bancarios.plan_pago_cuota_id). No editable ni borrable.
                        const cuotaPagada = c.estado === "pagado" || c.estado === "cobrado";
                        const estiloPagada = cuotaPagada ? { background: "#f5f5f5", color: "#999" } : {};
                        return (
                          <tr key={c.id ?? `nueva-${origIdx}`}>
                            <td>{c.numero_cuota ?? origIdx + 1}</td>
                            <td>
                              <SelectorFechaDocumento
                                value={c.fecha_vencimiento || ""}
                                onChange={(nuevaFecha) =>
                                  handleCambiarCuota(origIdx, "fecha_vencimiento", nuevaFecha || "")
                                }
                                pais={pais}
                                modo={MODOS_VALIDACION.DATO_DOCUMENTO}
                                disabled={cuotaPagada}
                                className="form-control form-control-sm"
                                style={estiloPagada}
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={c.monto_cuota || ""}
                                disabled={cuotaPagada}
                                onChange={(e) => handleCambiarCuota(origIdx, "monto_cuota", e.target.value)}
                                style={{ textAlign: "right", ...estiloPagada }}
                              />
                            </td>
                            <td style={{ textAlign: "center" }}>
                              <span
                                style={{
                                  fontSize: "0.7rem",
                                  padding: "2px 6px",
                                  borderRadius: 3,
                                  background: cuotaPagada ? "#d4edda" : "#fff3cd",
                                  color: cuotaPagada ? "#155724" : "#856404",
                                }}
                              >
                                {c.estado || "Pendiente"}
                              </span>
                            </td>
                            <td>
                              {!cuotaPagada && (
                                <button
                                  style={{ background: "none", border: "none", cursor: "pointer", color: "#c0392b" }}
                                  onClick={() => setCuotas((prev) => prev.filter((_, idx) => idx !== origIdx))}
                                >
                                  ×
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
                <div style={{ textAlign: "right", fontSize: "0.83rem", marginTop: 8 }}>
                  <span>
                    Total: <strong>{fmt(totalCuotas)}</strong>
                  </span>
                  {Math.abs(diferenciaCuotas) > 0 && (
                    <span style={{ marginLeft: 12, color: "#c0392b" }}>Diferencia: {fmt(diferenciaCuotas)}</span>
                  )}
                </div>
                {/* Reiniciar borra SOLO las pendientes; las pagadas se conservan (tienen un pago real
                    vinculado — borrarlas dejaría el pago huérfano). Se oculta si no hay pendientes. */}
                {hayPendientes && (
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    style={{ marginTop: 8 }}
                    title="Borra las cuotas pendientes; las pagadas se conservan"
                    onClick={() => setCuotas(cuotas.filter(esCuotaPagada))}
                  >
                    Reiniciar
                  </button>
                )}
              </>
            )}
          </>
        )}

        {/* ── Acción 3: Factoring (operación existente) ── */}
        {accion === "factoring" && operacionExistente && (
          <div style={{ padding: "12px 0" }}>
            <div
              style={{
                background: operacionExistente.estado === "activa" ? "#eafaf1" : "#f4f4f4",
                border: `1px solid ${operacionExistente.estado === "activa" ? "#27ae60" : "#ccc"}`,
                borderRadius: 6,
                padding: "12px 16px",
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  fontWeight: 600,
                  fontSize: "0.9rem",
                  marginBottom: 6,
                  color: operacionExistente.estado === "activa" ? "#1e8449" : "#666",
                }}
              >
                {operacionExistente.estado === "activa"
                  ? "✓ Operación de factoring activa"
                  : "Operación de factoring liquidada"}
              </div>
              <div style={{ fontSize: "0.82rem", color: "#555", display: "flex", flexDirection: "column", gap: 4 }}>
                <span>
                  <strong>Proveedor:</strong> {operacionExistente.proveedor?.nombre || "—"}
                </span>
                <span>
                  <strong>Giro a recibir:</strong> {fmt(Number(operacionExistente.giro_a_recibir || 0))}
                </span>
                <span>
                  <strong>% Anticipo:</strong>{" "}
                  {formatPctLocal(Number(operacionExistente.pct_anticipo || 0) * 100, pais, 1)}%
                </span>
                <span>
                  <strong>Tasa:</strong> {formatPctLocal(Number(operacionExistente.tasa || 0) * 100, pais, 4)}%
                </span>
                <span>
                  <strong>Vence:</strong> {operacionExistente.fecha_cobro_final || "—"}
                </span>
              </div>
            </div>
            <div style={{ fontSize: "0.8rem", color: "#888", textAlign: "center" }}>
              Para modificar o anular esta operación, usá el módulo de Factoring.
            </div>
          </div>
        )}

        {/* ── Acción 3: Factoring (nueva operación) ── */}
        {accion === "factoring" && !operacionExistente && (
          <div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
                Proveedor de factoring
              </label>
              <select
                className="form-control form-control-sm"
                value={formFactoring.proveedor_factoring_id}
                onChange={(e) => handleProveedorChange(e.target.value)}
              >
                <option value="">Seleccionar...</option>
                {proveedores.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nombre}
                    {p.tipo === "pronto_pago" ? " (pronto pago)" : ""}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
              <div>
                <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
                  Fecha operación
                </label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={formFactoring.fecha_operacion}
                  onChange={(e) => setFormFactoring((p) => ({ ...p, fecha_operacion: e.target.value }))}
                />
              </div>
              <div>
                <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
                  Fecha vencimiento
                </label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={formFactoring.fecha_cobro_final}
                  onChange={(e) => setFormFactoring((p) => ({ ...p, fecha_cobro_final: e.target.value }))}
                />
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
              <div>
                <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
                  % Anticipo
                </label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={formatPctDisplay(formFactoring.pct_anticipo)}
                  onChange={(e) => setFormFactoring((p) => ({ ...p, pct_anticipo: parsePctInput(e.target.value) }))}
                  placeholder="ej: 95"
                />
              </div>
              <div>
                <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
                  Tasa mensual (%)
                </label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={formatPctDisplay(formFactoring.tasa)}
                  onChange={(e) =>
                    setFormFactoring((p) => ({
                      ...p,
                      tasa: parsePctInput(e.target.value),
                      comision_proveedor: "",
                    }))
                  }
                  placeholder="ej: 2.67"
                />
              </div>
              <div>
                <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
                  Tasa mora (%)
                </label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={formatPctDisplay(formFactoring.tasa_mora)}
                  onChange={(e) => setFormFactoring((p) => ({ ...p, tasa_mora: parsePctInput(e.target.value) }))}
                  placeholder="ej: 3.79"
                />
              </div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
                Comisión proveedor ($) — fee operativo del proveedor
              </label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={formatMontoDisplay(formFactoring.comision_proveedor, pais)}
                onChange={(e) =>
                  setFormFactoring((p) => ({
                    ...p,
                    comision_proveedor: parseMontoInput(e.target.value, factura?.moneda || "CLP"),
                  }))
                }
                placeholder="ej: 48.790"
              />
            </div>
            <div
              style={{
                background: "#fff3cd",
                borderRadius: 6,
                padding: "10px 14px",
                marginBottom: 12,
                fontSize: "0.82rem",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", color: "#666" }}>
                <span>Monto anticipado ({formFactoring.pct_anticipo || 0}%):</span>
                <span>{fmt(capitalAdelantado)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", color: "#666", marginTop: 4 }}>
                <span>Plazo:</span>
                <span>{plazoDias} días</span>
              </div>
              {proveedorFactoringSel && (
                <div style={{ color: "#8a6d3b", fontSize: "0.72rem", marginTop: 4, fontStyle: "italic" }}>
                  {describirConvencionFactoring({
                    // Con dos fórmulas, la etiqueta muestra la elegida (o ninguna todavía: el
                    // comparador de abajo presenta las dos).
                    tipoTasa: eligeTasaPorComprobante ? tipoTasa || "por_comprobante" : proveedorFactoringSel.tipo_tasa,
                    redondeoPorDocumento: proveedorFactoringSel.redondeo_por_documento,
                    plazoDias,
                  })}
                </div>
              )}
              <div style={{ borderTop: "1px solid #e0c87a", margin: "8px 0" }} />
              {resultadosPorTasa ? (
                <ComparadorTasaFactoring
                  resultados={resultadosPorTasa}
                  comision={comisionProveedor}
                  seleccion={tipoTasaElegido}
                  onSeleccionar={setTipoTasaElegido}
                  fmt={fmt}
                />
              ) : (
                <>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span>Diferencia precio:</span>
                    <span style={{ color: "#c0392b" }}>{fmt(diferenciaPrecio)}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
                    <span>Comisión:</span>
                    <span style={{ color: "#c0392b" }}>{fmt(comisionProveedor)}</span>
                  </div>
                  <div style={{ borderTop: "1px solid #e0c87a", margin: "8px 0" }} />
                  <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 600 }}>
                    <span>Total cobros:</span>
                    <span style={{ color: "#c0392b" }}>{fmt(totalCobros)}</span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginTop: 6,
                      fontSize: "0.95rem",
                      fontWeight: 700,
                    }}
                  >
                    <span>GIRO A RECIBIR:</span>
                    <span style={{ color: "#2e7d32" }}>{fmt(giroARecibir)}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
                    <span>Excedentes:</span>
                    <span style={{ color: "#2980b9" }}>{fmt(excedentes)}</span>
                  </div>
                </>
              )}
            </div>
            {(() => {
              const monedaFactura = factura?.moneda || "CLP";
              const cuentasMoneda = cuentas.filter((c) => c.moneda === monedaFactura);

              // Si hay una sola cuenta en esa moneda, no mostrar el selector
              if (cuentasMoneda.length === 1) {
                return (
                  <div style={{ marginBottom: 12, fontSize: "0.82rem", color: "#555" }}>
                    <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
                      Cuenta bancaria destino
                    </label>
                    <span>{`${cuentasMoneda[0].bancos?.nombre || "Banco"} ${cuentasMoneda[0].moneda} — ${cuentasMoneda[0].numero_cuenta}`}</span>
                  </div>
                );
              }

              // Si hay múltiples cuentas en esa moneda, mostrar selector
              return (
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
                    Cuenta bancaria destino
                  </label>
                  <select
                    className="form-control form-control-sm"
                    value={formFactoring.cuenta_bancaria_id}
                    onChange={(e) => setFormFactoring((p) => ({ ...p, cuenta_bancaria_id: e.target.value }))}
                  >
                    <option value="">Seleccionar...</option>
                    {cuentasMoneda.map((c) => (
                      <option key={c.id} value={c.id}>
                        {`${c.bancos?.nombre || "Banco"} ${c.moneda} — ${c.numero_cuenta}`}
                      </option>
                    ))}
                  </select>
                </div>
              );
            })()}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          size="sm"
          disabled={loading}
          onClick={() => {
            if (accion === "fecha") handleGuardarFecha();
            else if (accion === "cuotas") handleGuardarCuotas();
            else if (accion === "factoring") handleGuardarFactoring();
          }}
        >
          {loading ? "Guardando..." : "Guardar"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PlanCuotasModal;
